import { createContext, useContext, type ReactNode } from 'react';
import { userSetupStepKeys, type UserSetupStepKey } from '@brimo/shared';
import { useIsMobile } from '../../../ds/hooks';
import {
  MobileWizardFrame,
  WizardButton,
  WizardFooter,
  WizardHeader,
  WizardIntro,
  WizardLayout,
  type WizardFooterAction,
} from '../ui';
import type { UserSetup } from './types';
import styles from './StepFrame.module.css';

/** Labels of the stepper circles (Nutzer 02–11: „Abrechnung" for the billing step). */
export const STEP_LABELS: Record<UserSetupStepKey, string> = {
  profile: 'Profil',
  mailbox: 'Postfach',
  calendar: 'Kalender',
  availability: 'Verfügbarkeit',
  whatsapp: 'WhatsApp',
  signature: 'Signatur',
  billing: 'Abrechnung',
  security: 'Sicherheit',
};

export const STEP_COUNT = userSetupStepKeys.length;

export function stepNumber(key: UserSetupStepKey): number {
  return userSetupStepKeys.indexOf(key) + 1;
}

/** „Schritt 1 von 8". */
export function stepLabel(key: UserSetupStepKey): string {
  return `Schritt ${stepNumber(key)} von ${STEP_COUNT}`;
}

/** What a step component needs from the wizard: the loaded state and the navigation. */
export interface WizardContextValue {
  setup: UserSetup;
  step: UserSetupStepKey;
  /** After „Weiter"/„Überspringen": the next step in order, after the last one the done page. */
  goNext: () => void;
  /** „Zurück"/„‹": the previous step (none on the first). */
  goBack: (() => void) | null;
  /** `POST /setup/user/steps/:key { action }`, then `goNext`; false when the server refused (see `actError`). */
  act: (action: 'continue' | 'skip') => Promise<boolean>;
  acting: 'continue' | 'skip' | null;
  /** The server's refusal of the last action („Es ist noch kein Postfach verbunden."). */
  actError: string | null;
}

export const WizardContext = createContext<WizardContextValue | null>(null);

export function useWizard(): WizardContextValue {
  const value = useContext(WizardContext);
  if (value === null) throw new Error('useWizard() outside the user wizard');
  return value;
}

export interface StepFooter {
  back?: WizardFooterAction | undefined;
  note?: ReactNode | undefined;
  secondary?: WizardFooterAction | undefined;
  hint?: ReactNode | undefined;
  primary?: WizardFooterAction | undefined;
}

export interface StepFrameProps {
  title: string;
  text?: ReactNode | undefined;
  /** „Schritt 7 von 8 · eigene Abrechnung"; defaults to „Schritt n von 8". */
  eyebrow?: string | undefined;
  /** Phone title/text (M2–M9 shorten both). */
  mobileTitle?: string | undefined;
  mobileText?: ReactNode | undefined;
  /** Column width of the desktop frame (760, 860, 900, 940). */
  width: number;
  gap?: 22 | 24 | undefined;
  footer: StepFooter | null;
  /** Phone footer: text button („Später") and primary (`flex: 1`); defaults to `footer.secondary`/`primary`. */
  mobileFooter?: {
    secondary?: WizardFooterAction | undefined;
    primary?: WizardFooterAction | undefined;
  } | null;
  /** Hides the intro (Nutzer 05 draws the two state cards only). */
  intro?: boolean | undefined;
  children: ReactNode;
}

/** Desktop: stepper header, centred column, intro, content, footer · phone: the 390-px frame with the segment bar. */
export function StepFrame({
  title,
  text,
  eyebrow,
  mobileTitle,
  mobileText,
  width,
  gap,
  footer,
  mobileFooter,
  intro = true,
  children,
}: StepFrameProps) {
  const mobile = useIsMobile();
  const { setup, step, goBack } = useWizard();

  if (mobile) {
    const phoneFooter = mobileFooter === undefined ? footer : mobileFooter;
    return (
      <MobileWizardFrame
        title={step === 'profile' ? 'DAMOKLES' : STEP_LABELS[step]}
        stepLabel={stepLabel(step)}
        segments={STEP_COUNT}
        current={stepNumber(step)}
        onBack={goBack ?? undefined}
        footer={
          phoneFooter && (phoneFooter.secondary || phoneFooter.primary) ? (
            <>
              {phoneFooter.secondary ? (
                <WizardButton
                  variant="ghost"
                  size="mobile"
                  onClick={phoneFooter.secondary.onClick}
                  disabled={phoneFooter.secondary.disabled}
                  loading={phoneFooter.secondary.loading}
                >
                  {phoneFooter.secondary.label}
                </WizardButton>
              ) : null}
              {phoneFooter.primary ? (
                <WizardButton
                  variant="primary"
                  size="mobile"
                  className={styles.mobilePrimary}
                  type={phoneFooter.primary.type ?? 'button'}
                  form={phoneFooter.primary.form}
                  onClick={phoneFooter.primary.onClick}
                  disabled={phoneFooter.primary.disabled}
                  loading={phoneFooter.primary.loading}
                >
                  {phoneFooter.primary.label}
                </WizardButton>
              ) : null}
            </>
          ) : undefined
        }
      >
        {intro ? (
          <div className={styles.mobileIntro}>
            <h2 className={styles.mobileTitle}>{mobileTitle ?? title}</h2>
            {(mobileText ?? text) ? <p className={styles.mobileText}>{mobileText ?? text}</p> : null}
          </div>
        ) : null}
        {children}
      </MobileWizardFrame>
    );
  }

  return (
    <WizardLayout
      width={width}
      gap={gap}
      header={
        <WizardHeader
          steps={setup.steps.map((item) => ({
            key: item.key,
            label: STEP_LABELS[item.key],
            status: item.status,
          }))}
          currentKey={step}
          userLabel={`Angemeldet als ${setup.user.email}`}
        />
      }
    >
      {intro ? <WizardIntro eyebrow={eyebrow ?? stepLabel(step)} title={title} text={text} /> : null}
      {children}
      {footer && footer.primary ? (
        <WizardFooter
          back={footer.back}
          note={footer.note}
          secondary={footer.secondary}
          hint={footer.hint}
          primary={footer.primary}
        />
      ) : null}
    </WizardLayout>
  );
}

/** The server's refusal of „Weiter" under the content („Es ist noch kein Postfach verbunden."). */
export function ActionError() {
  const { actError } = useWizard();
  if (!actError) return null;
  return (
    <p className={styles.actionError} role="alert">
      {actError}
    </p>
  );
}
